import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '@welldev-group/rcl-icon';
import { Link } from '@welldev-group/react-comp-lib';
import Button from '@welldev-group/rcl-button';
import IconButton from '@welldev-group/rcl-icon-button';
import ConfirmationModal from 'sharedWebpack/ConfirmationModal';
import {
  MAXIMUM_IMAGE_DISPLAY,
  RESPONSIVE_SLIDER_CONFIG,
} from 'sharedWebpack/helpers/constants/common';
import RoomDefaultImage from 'agentSyncedImages/fallback_images/product/product_xxl_hotel.png';
import LazyLoad from 'react-lazyload';
import ImageGallery from './ImageGallery';
import ExpandIcon from './supplierLogos/expandIcon.svg';

const RoomImageGallery = ({ images, travelAppLayout, roomType }) => {
  const [focusedImage, setFocusedImage] = useState(images[0]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  const noPreview = images.length <= 1;
  const extraImageCount = images.length - MAXIMUM_IMAGE_DISPLAY;

  const hasExtraImage = index => extraImageCount > 0 && index + 1 >= MAXIMUM_IMAGE_DISPLAY;
  const setPlaceholderImage = event => {
    const target = event.target;
    target.src = RoomDefaultImage;
  };
  const onModalOpen = () => setModalOpen(true);
  const onModalClose = () => setModalOpen(false);
  const isSelectedImage = index => index === selectedImage;

  const modalHeader = () => (
    <div className="room-image__header">
      <div className="room-image__header-title">{roomType}</div>
      <IconButton
        icon={<Icon name="close" size="medium" />}
        onClick={onModalClose}
        color="default"
        isIconOnly={true}
        className="cancel-icon"
        size="tiny"
      />
    </div>
  );

  const handleOnFocusImage = async (image, index) => {
    setFocusedImage(image);
    setSelectedImage(index);
  };

  const getModalBodyContent = () => {
    const roomImages = images.map(({ url, thumbnailUrl }) => ({
      original: url,
      thumbnail: thumbnailUrl,
    }));

    return (
      <div className="room-image">
        <ImageGallery
          images={roomImages}
          travelAppLayout={travelAppLayout}
          responsive={RESPONSIVE_SLIDER_CONFIG}
        />
      </div>
    );
  };

  return (
    <div className="room-image-gallery">
      <div
        className={classNames('room-image-gallery__focus', {
          'room-image-gallery__focus--full-height': noPreview,
        })}
      >
        <LazyLoad height="100%" once={true}>
          <img
            alt="Focused"
            className="room-image-gallery__focus-image"
            onError={setPlaceholderImage}
            src={focusedImage?.url || RoomDefaultImage}
            loading="lazy"
            key={focusedImage?.url}
          />
        </LazyLoad>
        <ConfirmationModal
          className="gallery-modal"
          confirmationHeader={modalHeader()}
          isModalOpen={modalOpen}
          isConfirmationOnly={false}
          withAction={false}
          onOutsideClick={onModalClose}
          subHeader={getModalBodyContent()}
          type={null}
        />
      </div>
      <span
        onClick={onModalOpen}
        role="button"
        onKeyDown={onModalOpen}
        tabIndex={0}
        className="room-image-gallery__focus-icon"
      >
        <img src={ExpandIcon} alt="expand-icon" />
      </span>
      {!noPreview && (
        <div className="room-image-gallery__preview">
          {images.slice(0, MAXIMUM_IMAGE_DISPLAY).map((image, index) => (
            <Link key={image?.url} onClick={() => handleOnFocusImage(image, index)} type="default">
              <LazyLoad height="100%" once={true}>
                <img
                  alt="Thumbnail"
                  onError={setPlaceholderImage}
                  className={classNames('room-image-gallery__preview-image', {
                    'room-image-gallery__preview-image--selected': isSelectedImage(index),
                  })}
                  src={image?.thumbnailUrl || RoomDefaultImage}
                  loading="lazy"
                />
              </LazyLoad>

              {hasExtraImage(index) && (
                <Button
                  label={<span>+{extraImageCount}</span>}
                  onClick={onModalOpen}
                  className="link__content-shadow"
                />
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

RoomImageGallery.defaultProps = {
  images: [],
};

RoomImageGallery.propTypes = {
  travelAppLayout: PropTypes.bool.isRequired,
  roomType: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      thumbnailUrl: PropTypes.string,
    }),
  ),
};

export default RoomImageGallery;
